// frontend/src/pages/UsersManagement.js
import React, { useState, useEffect, useCallback } from "react";
import api from "../api";

const ROLES = ["manager", "seller", "buyer", "group"];

const ROLE_COLORS = {
  manager: { bg: "#dbeafe", color: "#1d4ed8", label: "Manager" },
  seller: { bg: "#dcfce7", color: "#15803d", label: "Seller" },
  buyer: { bg: "#fef9c3", color: "#a16207", label: "Buyer" },
  group: { bg: "#f3e8ff", color: "#7e22ce", label: "Group" },
};

const inputCls = {
  width: "100%",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  padding: "9px 12px",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
  color: "#111",
};

// ── Styles defined at module level so UserForm can access them ────────────────
const s = {
  page: {
    minHeight: "100vh",
    background: "#f9fafb",
    fontFamily: "'Inter', sans-serif",
  },
  topbar: {
    background: "#111827",
    color: "#fff",
    padding: "0 28px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    zIndex: 50,
  },
  topLeft: { display: "flex", alignItems: "center", gap: 12 },
  backBtn: {
    background: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#fff",
    borderRadius: 8,
    padding: "6px 14px",
    fontSize: 13,
    cursor: "pointer",
    fontWeight: 600,
  },
  newBtn: {
    background: "#6366f1",
    border: "none",
    color: "#fff",
    borderRadius: 8,
    padding: "7px 16px",
    fontSize: 13,
    cursor: "pointer",
    fontWeight: 700,
  },
  content: { maxWidth: 1000, margin: "0 auto", padding: "28px 16px" },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    overflow: "hidden",
    marginBottom: 20,
  },
  cardHead: {
    padding: "14px 20px",
    borderBottom: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  shopLabel: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  shopIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "#ede9fe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 17,
  },
  userRow: {
    padding: "14px 20px",
    borderBottom: "1px solid #f9fafb",
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  userInfo: { flex: 1, minWidth: 0 },
  userName: { fontWeight: 700, fontSize: 14, color: "#111", margin: 0 },
  userEmail: { fontSize: 12, color: "#6b7280", margin: 0, marginTop: 1 },
  actions: { display: "flex", gap: 6, flexShrink: 0 },
  editBtn: {
    background: "#eff6ff",
    border: "none",
    color: "#1d4ed8",
    borderRadius: 7,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  toggleBtn: (active) => ({
    background: active ? "#fef2f2" : "#f0fdf4",
    border: "none",
    color: active ? "#dc2626" : "#16a34a",
    borderRadius: 7,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  }),
  deleteBtn: {
    background: "#fef2f2",
    border: "none",
    color: "#dc2626",
    borderRadius: 7,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  filterBar: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "14px 18px",
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 20,
  },
  selectEl: {
    borderRadius: 8,
    border: "1px solid #d1d5db",
    padding: "7px 12px",
    fontSize: 13,
    background: "#fff",
    outline: "none",
    color: "#111",
  },
  searchInput: {
    borderRadius: 8,
    border: "1px solid #d1d5db",
    padding: "7px 14px",
    fontSize: 13,
    outline: "none",
    flex: 1,
    minWidth: 180,
    color: "#111",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 18,
    padding: "28px 28px",
    width: "100%",
    maxWidth: 460,
    boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 5,
  },
  formRow: { marginBottom: 14 },
  submitBtn: {
    background: "#6366f1",
    border: "none",
    color: "#fff",
    borderRadius: 9,
    padding: "10px 22px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    marginRight: 10,
  },
  cancelBtn: {
    background: "#f3f4f6",
    border: "none",
    color: "#374151",
    borderRadius: 9,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  toast: (type) => ({
    background: type === "success" ? "#f0fdf4" : "#fef2f2",
    border: `1px solid ${type === "success" ? "#bbf7d0" : "#fecaca"}`,
    color: type === "success" ? "#166534" : "#991b1b",
    borderRadius: 10,
    padding: "11px 16px",
    fontSize: 13,
    marginBottom: 18,
  }),
};

// ── Badge ─────────────────────────────────────────────────────────────────────
function Badge({ role }) {
  const c = ROLE_COLORS[role] || {
    bg: "#f3f4f6",
    color: "#374151",
    label: role,
  };
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        borderRadius: 999,
        padding: "2px 10px",
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {c.label}
    </span>
  );
}

// ── Avatar ────────────────────────────────────────────────────────────────────
function Avatar({ name }) {
  const initials =
    name
      ?.split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "??";
  const colors = [
    "#6366f1",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
  ];
  const idx = name?.charCodeAt(0) % colors.length || 0;
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: colors[idx],
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

// ── UserForm — defined at MODULE LEVEL (outside UsersManagement) ──────────────
// This is the key fix: keeps the form stable across re-renders so typing works.
// All variables it needs are passed in as props instead of closing over them.
function UserForm({
  data,
  setData,
  onSubmit,
  title,
  isEdit,
  shops,
  saving,
  onCancel,
}) {
  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <h3 style={{ margin: "0 0 20px", fontSize: 18, fontWeight: 800 }}>
          {title}
        </h3>
        <form onSubmit={onSubmit}>
          <div style={s.formRow}>
            <label style={s.label}>Full Name *</label>
            <input
              style={inputCls}
              required
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              placeholder="John Mugisha"
            />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Email *</label>
            <input
              style={inputCls}
              required
              type="email"
              value={data.email}
              onChange={(e) => setData({ ...data, email: e.target.value })}
              placeholder="john@example.com"
            />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>
              {isEdit ? "New Password (leave blank to keep)" : "Password *"}
            </label>
            <input
              style={inputCls}
              type="password"
              required={!isEdit}
              value={isEdit ? data.newPassword || "" : data.password}
              onChange={(e) =>
                setData(
                  isEdit
                    ? { ...data, newPassword: e.target.value }
                    : { ...data, password: e.target.value },
                )
              }
              placeholder={
                isEdit ? "Leave blank to keep current" : "Min 6 characters"
              }
              minLength={isEdit ? 0 : 6}
            />
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Role *</label>
              <select
                style={{ ...inputCls }}
                required
                value={data.role}
                onChange={(e) => setData({ ...data, role: e.target.value })}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_COLORS[r]?.label || r}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Shop *</label>
              <select
                style={{ ...inputCls }}
                required
                value={data.shop_id}
                onChange={(e) => setData({ ...data, shop_id: e.target.value })}
              >
                <option value="">— Select Shop —</option>
                {shops.map((sh) => (
                  <option key={sh.id} value={sh.id}>
                    {sh.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <button type="submit" style={s.submitBtn} disabled={saving}>
              {saving
                ? "Saving…"
                : isEdit
                  ? "💾 Save Changes"
                  : "➕ Create User"}
            </button>
            {/* onCancel is passed in as a prop — no more direct reference to setEditUser/setShowForm */}
            <button type="button" style={s.cancelBtn} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function UsersManagement({ onBack }) {
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShop, setSelectedShop] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [msg, setMsg] = useState({ text: "", type: "success" });
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    name: "",
    email: "",
    password: "",
    role: "seller",
    shop_id: "",
  };
  const [form, setForm] = useState(emptyForm);

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "success" }), 5000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: shopData }, { data: userData }] = await Promise.all([
        api.get("/api/shops"),
        api.get("/api/users"),
      ]);
      setShops(shopData);
      setUsers(userData);
    } catch (err) {
      showMsg(
        "❌ Failed to load: " + (err.response?.data?.error || err.message),
        "error",
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Filtered users ──────────────────────────────────────────────────────
  const filtered = users.filter((u) => {
    const matchShop =
      selectedShop === "all" || String(u.shop_id) === String(selectedShop);
    const matchRole = roleFilter === "all" || u.role === roleFilter;
    const q = searchQ.toLowerCase();
    const matchQ =
      !q ||
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q);
    return matchShop && matchRole && matchQ;
  });

  // Group by shop
  const grouped = {};
  filtered.forEach((u) => {
    const key = u.shop_name || "No Shop";
    if (!grouped[key])
      grouped[key] = { shop_name: key, shop_id: u.shop_id, users: [] };
    grouped[key].users.push(u);
  });
  const groupedList = Object.values(grouped);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/users", form);
      showMsg("✅ User created successfully!");
      setShowForm(false);
      setForm(emptyForm);
      loadData();
    } catch (err) {
      showMsg(
        "❌ " + (err.response?.data?.error || "Failed to create user"),
        "error",
      );
    }
    setSaving(false);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: editUser.name,
        email: editUser.email,
        role: editUser.role,
        shop_id: editUser.shop_id,
      };
      if (editUser.newPassword) payload.password = editUser.newPassword;
      await api.put(`/api/users/${editUser.id}`, payload);
      showMsg("✅ User updated successfully!");
      setEditUser(null);
      loadData();
    } catch (err) {
      showMsg(
        "❌ " + (err.response?.data?.error || "Failed to update"),
        "error",
      );
    }
    setSaving(false);
  };

  const handleToggle = async (user) => {
    try {
      await api.put(`/api/users/${user.id}/toggle`);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, is_active: !u.is_active } : u,
        ),
      );
    } catch (err) {
      showMsg("❌ " + (err.response?.data?.error || "Failed"), "error");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/users/${deleteUser.id}`);
      showMsg("✅ User deleted");
      setDeleteUser(null);
      loadData();
    } catch (err) {
      showMsg(
        "❌ " + (err.response?.data?.error || "Failed to delete"),
        "error",
      );
    }
  };

  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={s.topbar}>
        <div style={s.topLeft}>
          <button style={s.backBtn} onClick={onBack}>
            ← Back
          </button>
          <span style={{ fontSize: 20 }}>👥</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>
              Users Management
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>
              Super Admin Portal
            </div>
          </div>
        </div>
        <button
          style={s.newBtn}
          onClick={() => {
            setShowForm(true);
            setForm(emptyForm);
          }}
        >
          + New User
        </button>
      </div>

      <div style={s.content}>
        {/* Toast */}
        {msg.text && <div style={s.toast(msg.type)}>{msg.text}</div>}

        {/* Stats row */}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          {[
            {
              label: "Total Users",
              value: users.length,
              icon: "👥",
              color: "#6366f1",
            },
            {
              label: "Active",
              value: users.filter((u) => u.is_active).length,
              icon: "✅",
              color: "#16a34a",
            },
            {
              label: "Shops",
              value: shops.length,
              icon: "🏪",
              color: "#f59e0b",
            },
            {
              label: "Showing",
              value: filtered.length,
              icon: "🔍",
              color: "#8b5cf6",
            },
          ].map(({ label, value, icon, color }) => (
            <div
              key={label}
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: "16px 20px",
                flex: "1 1 140px",
              }}
            >
              <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color }}>
                {value}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={s.filterBar}>
          <input
            style={s.searchInput}
            placeholder="🔍  Search by name or email…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
          <select
            style={s.selectEl}
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            <option value="all">All Shops</option>
            {shops.map((sh) => (
              <option key={sh.id} value={sh.id}>
                🏪 {sh.name}
              </option>
            ))}
          </select>
          <select
            style={s.selectEl}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_COLORS[r]?.label || r}
              </option>
            ))}
          </select>
          <button
            onClick={loadData}
            style={{
              background: "#f3f4f6",
              border: "none",
              borderRadius: 8,
              padding: "7px 14px",
              fontSize: 13,
              cursor: "pointer",
              color: "#374151",
              fontWeight: 600,
            }}
          >
            ↻ Refresh
          </button>
        </div>

        {/* Users list */}
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}
          >
            Loading users…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
            <div style={{ fontWeight: 700, color: "#374151", marginBottom: 4 }}>
              No users found
            </div>
            <div style={{ fontSize: 13, color: "#9ca3af" }}>
              Try adjusting filters or create a new user
            </div>
          </div>
        ) : (
          groupedList.map((group) => (
            <div key={group.shop_name} style={s.card}>
              <div style={s.cardHead}>
                <div style={s.shopLabel}>
                  <div style={s.shopIcon}>🏪</div>
                  <div>
                    <div
                      style={{ fontWeight: 800, fontSize: 15, color: "#111" }}
                    >
                      {group.shop_name}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {group.users.length} user
                      {group.users.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
              </div>

              {group.users.map((user) => (
                <div
                  key={user.id}
                  style={{
                    ...s.userRow,
                    opacity: user.is_active ? 1 : 0.55,
                    background: user.is_active ? "#fff" : "#fafafa",
                  }}
                >
                  <Avatar name={user.name} />
                  <div style={s.userInfo}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <p style={s.userName}>{user.name}</p>
                      <Badge role={user.role} />
                      {!user.is_active && (
                        <span
                          style={{
                            fontSize: 10,
                            background: "#fee2e2",
                            color: "#dc2626",
                            borderRadius: 999,
                            padding: "1px 8px",
                            fontWeight: 700,
                          }}
                        >
                          INACTIVE
                        </span>
                      )}
                    </div>
                    <p style={s.userEmail}>{user.email}</p>
                  </div>
                  <div style={s.actions}>
                    <button
                      style={s.editBtn}
                      onClick={() => setEditUser({ ...user, newPassword: "" })}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      style={s.toggleBtn(user.is_active)}
                      onClick={() => handleToggle(user)}
                    >
                      {user.is_active ? "⏸ Disable" : "▶ Enable"}
                    </button>
                    <button
                      style={s.deleteBtn}
                      onClick={() => setDeleteUser(user)}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* ── Create User Modal ── */}
      {showForm && (
        <UserForm
          data={form}
          setData={setForm}
          onSubmit={handleCreate}
          title="➕ Create New User"
          isEdit={false}
          shops={shops}
          saving={saving}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* ── Edit User Modal ── */}
      {editUser && (
        <UserForm
          data={editUser}
          setData={setEditUser}
          onSubmit={handleEdit}
          title="✏️ Edit User"
          isEdit={true}
          shops={shops}
          saving={saving}
          onCancel={() => setEditUser(null)}
        />
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteUser && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, maxWidth: 380, textAlign: "center" }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 800 }}>
              Delete User?
            </h3>
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 22px" }}>
              Are you sure you want to delete <strong>{deleteUser.name}</strong>
              ? This action <strong>cannot be undone</strong>.
            </p>
            <button
              onClick={handleDelete}
              style={{ ...s.submitBtn, background: "#dc2626", marginRight: 10 }}
            >
              Yes, Delete
            </button>
            <button onClick={() => setDeleteUser(null)} style={s.cancelBtn}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
